/**
 * Cart drawer Wishlist tab — reads Wysh wishlist data and renders products.
 */
import {
  WYSH_CACHE_KEY,
  defaultWishlistKey,
  proxyPath,
  readWyshWishlists,
  removeFromWysh,
} from '@theme/fluevog-wishlist';
import {
  addVariantToCart,
  displayTitle,
  familyName,
  fetchWishlistProducts,
  numericId,
  pickVariant,
  readWishlistEntries,
} from '@theme/fluevog-wishlist-products';
import { formatMoneyParts } from '@theme/money-formatting';

const ROOT_SELECTOR = '[data-fv-cart-wishlist]';
const COUNT_SELECTOR = '[data-fv-cart-wishlist-count]';

/**
 * Signed in: the account wishlist tab (every list; set in header-actions).
 * Guests: deep-link into the wishlist itself — skip Wysh’s “My Lists” overview.
 */
const wishlistUrl = () => {
  if (window.fvAccountWishlistUrl) return window.fvAccountWishlistUrl;
  const key = readWyshWishlists().find((list) => list?.wishlist_key)?.wishlist_key;
  return proxyPath(key ? `/${key}` : '/');
};

const getMoneyFormat = () => {
  const root = document.querySelector(ROOT_SELECTOR);
  return (
    root?.getAttribute('data-money-format') ||
    window.Shopify?.money_format ||
    '${{amount}}'
  );
};

const attrLines = (product, variant, { sizeSelected = false } = {}) => {
  const lines = [];
  const options = variant?.selectedOptions || [];
  const colorOption = options.find((option) => {
    const name = (option.name || '').toLowerCase();
    return name.includes('color') || name.includes('colour');
  });
  const sizeOption = options.find((option) => (option.name || '').toLowerCase().includes('size'));

  let colorValue = colorOption?.value;
  if (!colorValue || colorValue === 'Default Title') {
    const titleParts = String(product.title || '').split(' - ');
    if (titleParts.length > 1) colorValue = titleParts.slice(1).join(' - ');
  }
  if (colorValue && colorValue !== 'Default Title') {
    lines.push(
      `<div class="fv-cart-attr"><dt>Colour:</dt><dd>${escapeHtml(
        colorValue.charAt(0).toUpperCase() + colorValue.slice(1)
      )}</dd></div>`
    );
  }

  // Only show a size value when Wysh stored a specific variant (shopper picked a size).
  // Product-level wishlist items leave variant_ids empty — prompt for a size instead.
  if (sizeOption?.value && sizeOption.value !== 'Default Title') {
    const label = escapeHtml(sizeOption.name);
    const value = sizeSelected
      ? escapeHtml(sizeOption.value)
      : `<a class="fv-cart-attr__link" href="${escapeHtml(wishlistUrl())}">Select size</a>`;
    lines.push(`<div class="fv-cart-attr"><dt>${label}:</dt><dd>${value}</dd></div>`);
  }

  if (sizeSelected) {
    options.forEach((option) => {
      const name = (option.name || '').toLowerCase();
      if (name.includes('color') || name.includes('colour') || name.includes('size')) return;
      if (!option.value || option.value === 'Default Title') return;
      lines.push(
        `<div class="fv-cart-attr"><dt>${escapeHtml(option.name)}:</dt><dd>${escapeHtml(
          option.value
        )}</dd></div>`
      );
    });
  }

  return lines.join('');
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const CLOSE_ICON = `<span class="svg-wrapper" aria-hidden="true"><svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.9992 19.8224C20.0563 19.8651 20.1271 19.8885 20.1993 19.8841C20.2714 19.8797 20.3385 19.8482 20.3864 19.7959C20.4344 19.7435 20.4599 19.674 20.458 19.6017C20.4562 19.5295 20.4267 19.4609 20.3792 19.4077C20.3792 19.4077 20.3792 19.4077 20.3792 19.4077C20.1692 19.1729 19.9582 18.9393 19.7462 18.7068C17.8375 16.6142 15.8434 14.615 13.7638 12.7091C11.6841 10.8031 9.51896 8.99051 7.26828 7.27119C7.01821 7.08016 6.76708 6.89027 6.51489 6.70154C6.45776 6.65885 6.38691 6.63542 6.31476 6.63988C6.24263 6.64431 6.17556 6.67578 6.1276 6.7281C6.07965 6.78043 6.05414 6.84998 6.056 6.92222C6.05784 6.99449 6.08734 7.06303 6.13484 7.11623C6.3448 7.35104 6.55582 7.5847 6.76788 7.8172C8.67651 9.90974 10.6706 11.909 12.7503 13.8149C14.8299 15.7208 16.9951 17.5335 19.2458 19.2528C19.4958 19.4438 19.747 19.6337 19.9992 19.8224Z" fill="currentColor"/><path d="M6.03586 19.2838C5.9875 19.3362 5.95689 19.4043 5.95388 19.4765C5.95084 19.5487 5.97522 19.6187 6.02231 19.6718C6.06941 19.7249 6.13596 19.7574 6.20801 19.763C6.28008 19.7687 6.3513 19.7464 6.40912 19.7046C6.40912 19.7046 6.40912 19.7046 6.40912 19.7046C6.66434 19.52 6.91853 19.3343 7.17167 19.1473C9.45 17.4648 11.6443 15.6876 13.7547 13.8158C15.8651 11.9439 17.8914 9.97735 19.8338 7.91612C20.0496 7.68709 20.2644 7.4569 20.4782 7.22554C20.5265 7.17311 20.5572 7.10507 20.5602 7.03283C20.5632 6.96063 20.5388 6.89067 20.4917 6.83757C20.4446 6.78448 20.3781 6.75192 20.306 6.74632C20.234 6.74069 20.1627 6.76297 20.1049 6.80472C19.8497 6.98933 19.5955 7.1751 19.3424 7.36205C17.064 9.04455 14.8697 10.8217 12.7593 12.6936C10.649 14.5655 8.62261 16.532 6.68022 18.5932C6.4644 18.8223 6.24961 19.0525 6.03586 19.2838Z" fill="currentColor"/></svg></span>`;

const cartIconHtml = () => {
  const template = document.getElementById('fv-cart-wishlist-icons');
  const source = template?.content?.querySelector('[data-fv-icon="cart"] svg');
  if (!source) return '';
  return `<span class="svg-wrapper fv-cart-item__add-to-cart-icon" aria-hidden="true">${source.outerHTML}</span>`;
};

const renderEmpty = (root) => {
  root.innerHTML = `
    <div class="fv-cart-wishlist-placeholder">
      <h3 class="fv-cart-wishlist-placeholder__title">Wishlist</h3>
      <p class="fv-cart-wishlist-placeholder__text">
        Tap the heart on your favourite items, and they'll appear here.
      </p>
      <a class="fv-cart-wishlist__manage" href="${wishlistUrl()}">View wishlist</a>
    </div>
  `;
};

const renderItems = (root, productsById, entries, families) => {
  const cards = entries
    .map((entry) => {
      const product = productsById.get(entry.productId);
      if (!product) return '';

      const { variant, sizeSelected } = pickVariant(product, entry.variantIds);
      const variantId = variant ? numericId(variant.id) : '';
      // Only add a specific size to cart when one was saved on the wishlist item.
      const addVariantId = sizeSelected ? variantId : '';
      const image = variant?.image?.url || product.featuredImage?.url || '';
      const price = variant?.price || product.priceRange?.minVariantPrice;
      const money = price
        ? formatMoneyParts(
            price.amount,
            price.currencyCode || window.Shopify?.currency?.active || 'USD',
            getMoneyFormat()
          )
        : null;
      const href = sizeSelected && variantId
        ? `/products/${product.handle}?variant=${variantId}`
        : `/products/${product.handle}`;
      const title = displayTitle(product.title || '');
      const family = familyName(product, families);

      return `
        <li
          class="fv-cart-item"
          data-product-id="${escapeHtml(entry.productId)}"
          data-variant-id="${escapeHtml(addVariantId)}"
          data-wishlist-key="${escapeHtml(entry.wishlistKey || '')}"
        >
          <div class="fv-cart-item__media">
            <a href="${escapeHtml(href)}" class="cart-items__media-container">
              ${
                image
                  ? `<img class="cart-items__media-image" src="${escapeHtml(image)}" alt="${escapeHtml(
                      title
                    )}" width="140" height="140" loading="lazy">`
                  : ''
              }
            </a>
          </div>

          <div class="fv-cart-item__body">
            <div class="fv-cart-item__head">
              <div class="fv-cart-item__heading">
                ${family ? `<p class="fv-cart-item__family">${escapeHtml(family)}</p>` : ''}
                <a href="${escapeHtml(href)}" class="cart-items__title">${escapeHtml(title)}</a>
              </div>
              ${
                money
                  ? `<span class="fv-cart-price fv-cart-item__price">
                      <span class="fv-cart-price__amount">${money.amountHtml}</span>
                      <span class="fv-cart-price__currency">${escapeHtml(money.currency)}</span>
                    </span>`
                  : ''
              }
            </div>

            <dl class="fv-cart-attrs">
              ${attrLines(product, variant, { sizeSelected })}
            </dl>
          </div>

          <div class="fv-cart-item__actions">
            <button
              type="button"
              class="button button-unstyled fv-cart-item__add-to-cart"
              data-fv-add-to-cart
              data-variant-id="${escapeHtml(addVariantId)}"
              ${addVariantId ? '' : 'disabled'}
              ${addVariantId ? '' : `title="Select a size in your wishlist"`}
            >
              ${cartIconHtml()}
              <span>Add to Cart</span>
            </button>
            <button
              type="button"
              class="button button-unstyled fv-cart-item__remove"
              data-fv-wishlist-remove
              data-product-id="${escapeHtml(entry.productId)}"
              data-variant-id="${escapeHtml(sizeSelected ? variantId : '')}"
              aria-label="Remove ${escapeHtml(title)} from wishlist"
            >
              ${CLOSE_ICON}
              <span>Remove</span>
            </button>
          </div>
        </li>
      `;
    })
    .filter(Boolean)
    .join('');

  if (!cards) {
    renderEmpty(root);
    return;
  }

  root.innerHTML = `
    <scroll-hint class="cart-drawer__content fv-cart-wishlist__scroll">
      <ul class="fv-cart-items" role="list">${cards}</ul>
      <div class="fv-cart-wishlist__footer">
        <a class="fv-cart-wishlist__manage" href="${wishlistUrl()}">Manage wishlist</a>
      </div>
    </scroll-hint>
  `;
};

const addToCart = async (button) => {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  button.disabled = true;
  try {
    await addVariantToCart(variantId, 1, 'fv-cart-wishlist');
  } catch (error) {
    console.error(error);
    button.disabled = false;
  }
};

/** Remove from the list this item belongs to, not just the first list. */
const removeFromWishlist = async (button) => {
  const productId = button.dataset.productId;
  if (!productId || button.disabled) return;

  button.disabled = true;
  try {
    const wishlistKey = button.closest('[data-wishlist-key]')?.dataset.wishlistKey;
    await removeFromWysh(productId, button.dataset.variantId || '', wishlistKey || defaultWishlistKey());
  } catch (error) {
    console.error(error);
    button.disabled = false;
  } finally {
    scheduleRender({ force: true });
  }
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const removeButton = target?.closest('[data-fv-wishlist-remove]');
  if (removeButton?.closest(ROOT_SELECTOR)) {
    event.preventDefault();
    removeFromWishlist(removeButton);
    return;
  }

  const addButton = target?.closest('[data-fv-add-to-cart]');
  if (addButton?.closest(ROOT_SELECTOR)) {
    event.preventDefault();
    addToCart(addButton);
  }
});

const updateCount = (total) => {
  const value = Number.isFinite(Number(total)) ? Number(total) : 0;
  document.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
    el.hidden = false;
    el.removeAttribute('hidden');
    el.textContent = `(${value})`;
  });
};

const syncCountFromCache = () => {
  updateCount(readWishlistEntries().length);
};

const entriesSignature = (entries) =>
  entries
    .map((entry) => `${entry.productId}:${entry.variantIds.join(',')}`)
    .sort()
    .join('|');

let renderToken = 0;
let lastSignature = null;
let hasRendered = false;
let renderQueued = false;

const renderAll = async ({ force = false } = {}) => {
  const entries = readWishlistEntries();
  const signature = entriesSignature(entries);
  updateCount(entries.length);

  const roots = [...document.querySelectorAll(ROOT_SELECTOR)];
  if (!roots.length) return;

  // Skip full rebuild when nothing changed (stops the reload flicker)
  const rootsNeedPaint = roots.some(
    (root) => !root.querySelector('.fv-cart-items .fv-cart-item, .fv-cart-wishlist-placeholder')
  );
  if (!force && hasRendered && signature === lastSignature && !rootsNeedPaint) return;

  const token = ++renderToken;
  lastSignature = signature;

  const showLoading = !hasRendered;
  if (showLoading) {
    roots.forEach((root) => {
      root.innerHTML = `<p class="fv-cart-wishlist__loading">Loading wishlist…</p>`;
    });
  }

  if (!entries.length) {
    roots.forEach(renderEmpty);
    hasRendered = true;
    return;
  }

  try {
    const { productsById, families } = await fetchWishlistProducts(entries);
    if (token !== renderToken) return;
    roots.forEach((root) => renderItems(root, productsById, entries, families));
    hasRendered = true;
  } catch (error) {
    console.error(error);
    if (token !== renderToken) return;
    roots.forEach((root) => {
      root.innerHTML = `
        <div class="fv-cart-wishlist-placeholder">
          <h3 class="fv-cart-wishlist-placeholder__title">Wishlist</h3>
          <p class="fv-cart-wishlist-placeholder__text">
            Couldn’t load wishlist items right now.
          </p>
          <a class="fv-cart-wishlist__manage" href="${wishlistUrl()}">Open wishlist</a>
        </div>
      `;
    });
    hasRendered = true;
  }
};

const scheduleRender = (options) => {
  if (renderQueued) return;
  renderQueued = true;
  window.requestAnimationFrame(() => {
    renderQueued = false;
    renderAll(options);
  });
};

const isWishlistPanelActive = () =>
  [...document.querySelectorAll('.fv-cart-panel[id*="wishlist"]')].some((panel) => !panel.hidden);

document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('.fv-cart-tab') : null;
  if (tab?.dataset.tabTarget?.includes('wishlist')) scheduleRender();
});

window.addEventListener('storage', (event) => {
  if (event.key === WYSH_CACHE_KEY && isWishlistPanelActive()) scheduleRender({ force: true });
});

document.addEventListener('cart:update', () => {
  // Drawer HTML can remorph after cart changes — re-paint WISHLIST(n).
  syncCountFromCache();
  if (isWishlistPanelActive()) scheduleRender();
});

// Same-tab Wysh cache writes don't fire the storage event — listen directly
const nativeSetItem = localStorage.setItem.bind(localStorage);
localStorage.setItem = (key, value) => {
  const previous = key === WYSH_CACHE_KEY ? localStorage.getItem(WYSH_CACHE_KEY) : null;
  nativeSetItem(key, value);
  if (key === WYSH_CACHE_KEY && value !== previous) {
    syncCountFromCache();
    if (isWishlistPanelActive()) scheduleRender({ force: true });
  }
};

const observeDrawer = () => {
  const dialog = document.querySelector('cart-drawer-component dialog');
  if (!dialog) return;

  const onOpenChange = () => {
    if (dialog.open) {
      syncCountFromCache();
      if (isWishlistPanelActive()) scheduleRender();
    }
  };

  onOpenChange();
  new MutationObserver(onOpenChange).observe(dialog, {
    attributes: true,
    attributeFilter: ['open'],
  });
};

// Always paint WISHLIST(n) immediately — don't wait for drawer open / panel paint.
syncCountFromCache();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncCountFromCache();
    observeDrawer();
  });
} else {
  observeDrawer();
}
